import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [demos, setDemos] = useState([]);
  const [showDemos, setShowDemos] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/demo-accounts").then((r) => setDemos(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      const amden = ["amden_super_admin", "amden_consultant"].includes(user.role);
      navigate(amden ? "/portfolio" : "/", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      const amden = ["amden_super_admin", "amden_consultant"].includes(u.role);
      navigate(amden ? "/portfolio" : "/", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="flex min-h-screen bg-[#F4F6F9]">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1729] p-12 lg:flex">
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #FFB000 0, transparent 45%), radial-gradient(circle at 80% 70%, #4A78A8 0, transparent 40%)",
          }}
        />
        <div className="relative z-10">
          <Logo variant="dark" />
        </div>
        <div className="relative z-10 max-w-md">
          <div className="label-eyebrow mb-4 text-[#FFC24D]">Executive Business Operating System</div>
          <h1 className="font-display text-4xl font-extrabold leading-tight text-white">
            Plan clearly. Act consistently. Grow with purpose.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/60">
            Amden Compass turns consultancy advice, coaching and strategy into clear weekly actions and
            measurable results — so you always know where your business is going, how it's performing and
            what to do next.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {["Business Health", "My Plan", "Weekly Accountability", "KPI Scorecards", "Ask Amden"].map(
              (t) => (
                <span
                  key={t}
                  className="rounded-full border border-white/15 px-3 py-1 text-xs font-medium text-white/70"
                >
                  {t}
                </span>
              )
            )}
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/30">© {new Date().getFullYear()} Amden Group</div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <Logo variant="light" />
          </div>
          <div className="label-eyebrow mb-2">Welcome back</div>
          <h2 className="font-display text-3xl font-bold text-[#0B1729]">Sign in to Compass</h2>
          <p className="mt-2 text-sm text-[#1E2D44]/60">
            Access your business health, plans, actions and coaching.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#1E2D44]">Email address</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.ie"
                className="mt-1.5 h-11 bg-white"
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#1E2D44]">Password</Label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  data-testid="forgot-password-link"
                  className="text-xs font-medium text-[#B87F1E] hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-11 bg-white"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-center gap-2 rounded-lg bg-[#C95555]/10 px-3 py-2.5 text-sm text-[#C95555]"
              >
                <Icons.CircleAlert size={16} /> {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="h-11 w-full bg-[#FFB000] text-base font-semibold text-[#0B1729] hover:bg-[#E69E00]"
            >
              {loading ? <Icons.Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </Button>
          </form>

          {/* Demo panel */}
          <div className="mt-8 rounded-xl border border-[#E4E8EF] bg-white/60 p-4">
            <button
              onClick={() => setShowDemos((s) => !s)}
              className="flex w-full items-center justify-between text-left"
              data-testid="toggle-demo-accounts"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-[#0B1729]">
                <Icons.KeyRound size={15} className="text-[#FFB000]" /> Demonstration accounts
              </span>
              <Icons.ChevronDown
                size={16}
                className={`text-[#1E2D44]/40 transition-transform ${showDemos ? "rotate-180" : ""}`}
              />
            </button>
            {showDemos && (
              <div className="mt-3 space-y-1.5">
                {demos.map((d) => (
                  <button
                    key={d.email}
                    onClick={() => quickFill(d)}
                    data-testid={`demo-${d.email}`}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors hover:bg-[#F4F6F9]"
                  >
                    <div>
                      <div className="text-xs font-semibold text-[#0B1729]">{d.label}</div>
                      <div className="text-[11px] text-[#1E2D44]/50">{d.email}</div>
                    </div>
                    <span className="text-[10px] font-medium text-[#FFB000]">Use →</span>
                  </button>
                ))}
                <p className="px-3 pt-1 text-[10px] text-[#1E2D44]/40">
                  Passwords are pre-filled. This panel is for demonstration only.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} initialEmail={email} />
    </div>
  );
}

function ForgotPasswordDialog({ open, onClose, initialEmail }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { if (open) { setEmail(initialEmail || ""); setSent(false); } }, [open, initialEmail]);
  const submit = async () => {
    if (!email.trim()) return toast.error("Enter your email address");
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>Enter your email and we'll send you a secure link to set a new password.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="flex items-start gap-3 rounded-lg bg-[#3D8B65]/10 px-4 py-3.5 text-sm text-[#2E6B4E]" data-testid="forgot-password-sent">
            <Icons.MailCheck size={18} className="mt-0.5 shrink-0" />
            <span>If an account exists for <span className="font-medium">{email}</span>, a reset link is on its way. Check your inbox (and spam folder).</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="forgot-email">Email address</Label>
              <Input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.ie" className="mt-1.5 h-11 bg-white" data-testid="forgot-email" />
            </div>
          </div>
        )}
        <DialogFooter>
          {sent ? (
            <Button onClick={onClose} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="forgot-close">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={sending} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="forgot-submit">
                {sending ? <Icons.Loader2 className="animate-spin" size={16} /> : "Send reset link"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
